/**
 * The chief, per session: propose the next step, let the operator edit it, send.
 *
 * A long-running agent finishes a turn and goes quiet; the question is what to
 * say to it next. This reads the session's last turn (already on the row), and
 * on demand asks the model to analyse the transcript and propose the next
 * prompt (ADR-0017). The operator sees the proposal, edits it if they want, and
 * sends it — nothing is typed without that. The send goes through the driver,
 * which types under the control lease and so never types over a person.
 *
 * A deterministic proposal shows instantly, so the card is never empty while an
 * analysis is in flight; the model proposal replaces it when it lands, and says
 * it was the model rather than a rule.
 *
 * ---------------------------------------------------------------------------
 * What it looked like, and why it was rebuilt
 *
 * It was a stack: badge, then a button, then a sentence, then a field, then a
 * button under that. Five rows of one item each, every one starting at the same
 * left edge, on a row 3440px wide — so the two controls an operator actually
 * uses sat 260px apart at opposite corners of a block that filled a fifth of the
 * width and left the rest empty. The badge is 22px and the button beside it 26,
 * and nothing lined either of them up with anything.
 *
 * It is now one object with a header, a body and a footer, held to a measure:
 * the state and the reason on one line with `analyse` at its trailing edge, what
 * the agent actually said, the prompt, and a footer whose send button sits under
 * the field's right edge. Two controls, diagonally opposite, on the same two
 * vertical rules as the text between them.
 *
 * And it answers with the keyboard. Sending meant reaching for the mouse on
 * every session, one after another, which is the whole job — ⌘/Ctrl+Enter sends,
 * Escape gives the row back. Enter alone stays a newline, because a prompt an
 * operator is composing is prose and losing it to a stray keystroke is worse
 * than reaching for a modifier.
 */

import { useState, type KeyboardEvent } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { Send, Sparkles } from 'lucide-react'

import { api, ApiError } from '@/api/client'
import { Icon } from '@/components/icon'
import { Badge, Button } from '@/components/primitives'
import { Notice, TextAreaField, useApiErrorMessage } from '@/components/controls'
import { useEditable } from '@/hooks/useEditable'
import { chiefNoteSentence, chiefReasonSentence } from '@/lib/chiefReason'
import { useT, useLocale } from '@/i18n/context'
import { formatRelative } from '@/i18n'
import { turnSegments } from '@/lib/turnText'
import { Segments } from './lastTurn'
import type { LastTurn } from './wall'
import type { ID } from '@/types/domain'
import './nextStep.css'

type ChiefAction = 'CONTINUE' | 'ANSWER' | 'FINISH' | 'HOLD'

/** Mirrors api.ChiefProposalResponse. The reason travels as a kind plus its
 *  parameters so the sentence can be composed in the reader's language; the
 *  `reasoning` string is the server's English rendering, and is what a model's
 *  own reasoning — which has no kind — arrives as. */
interface ChiefProposal {
  sessionId: ID
  action: ChiefAction
  prompt?: string
  reasoning?: string
  reasonKind?: string
  reasonParams?: Record<string, string>
  noteKind?: string
  noteParams?: Record<string, string>
  /** The server's own sentence about the note — which account is designated and
   *  what is wrong with it. Not translated, so it is shown as the machine's
   *  words beside the translated note rather than interpolated into it. */
  noteDetail?: string
  /** The manager's candidates, best first. Absent when the rules answered:
   *  their presence is what says a model judged this session (ADR-0025). */
  options?: ChiefOption[]
  /** The account the analysis was spent on. */
  accountLabel?: string
  source: 'RULES' | 'MODEL'
}

/** Mirrors api.ChiefOption. Label and Why are the model's own words, in the
 *  language the request asked for, so they are shown verbatim — the console
 *  cannot translate a sentence it did not write. */
interface ChiefOption {
  action: ChiefAction
  label?: string
  prompt?: string
  why?: string
}

/** The action in words.
 *
 *  Switched rather than interpolated into a key, so the i18n contract stays a
 *  compile-time one — the same rule ApprovalCard's `ruleLabel` keeps. Built
 *  from `chief.action.${action}` it rendered the literal text
 *  `chief.action.undefined` in the badge the moment a response arrived without
 *  one, and would do the same for an action added to the server after this
 *  build. An action this build does not know has no badge, which is a fact;
 *  a missing-key placeholder is not. */
function actionLabel(a: ChiefAction | undefined, t: ReturnType<typeof useT>): string {
  switch (a) {
    case 'CONTINUE':
      return t('chief.action.CONTINUE')
    case 'ANSWER':
      return t('chief.action.ANSWER')
    case 'FINISH':
      return t('chief.action.FINISH')
    case 'HOLD':
      return t('chief.action.HOLD')
  }
  return ''
}

/**
 * What a chip is called.
 *
 * The model is asked for two or three words and usually gives them, but an
 * option without a label is still an option: the first line of the instruction
 * itself is a worse name and an honest one. What this must never do is invent
 * "Seçenek 2" — a number is not a description, and a list of numbers is a list
 * an operator has to open one at a time to read.
 */
function optionLabel(o: ChiefOption): string {
  if (o.label) return o.label
  const first = (o.prompt ?? '').split('\n')[0]?.trim() ?? ''
  return first.length > 56 ? first.slice(0, 55) + '…' : first
}

function actionTone(a: ChiefAction) {
  switch (a) {
    case 'ANSWER':
      return 'info' as const
    case 'FINISH':
      return 'ok' as const
    case 'HOLD':
      return 'neutral' as const
    default:
      return 'accent' as const
  }
}

/**
 * The next-step control for one session. `onSent` is called after a prompt is
 * typed, so the caller can refetch the session (its activity will move).
 */
export function NextStep({
  sessionId,
  turn,
  autoFocus,
  onSent,
  onDismiss,
}: {
  sessionId: ID
  /** What the agent last said. The row above shows one clipped line of it; this
   *  is the thing being replied to, so the editor shows it whole. */
  turn?: LastTurn
  /** Focus the prompt on mount. Set only when the operator opened this row
   *  themselves — the board opens one row by itself, and that one must not take
   *  the keyboard off the page. */
  autoFocus?: boolean
  onSent?: () => void
  /** Escape, and a successful send: give the row back. */
  onDismiss?: () => void
}) {
  const t = useT()
  const { locale } = useLocale()
  const describe = useApiErrorMessage()

  /* Which option the operator is looking at.
   *
   * Zero is not a choice made on their behalf: the list arrives ordered best
   * first and the prompt field is already seeded from that same first entry, so
   * the highlight names what the form holds rather than pre-selecting something
   * for them. Picking another is a human decision, which is why it writes
   * through `draft.set` — and why, from then on, no arriving proposal may take
   * it back (CLAUDE.md). */
  const [picked, setPicked] = useState(0)

  // The instant, free proposal from state.
  const suggestion = useQuery({
    queryKey: ['proposal', sessionId],
    queryFn: () => api.get<ChiefProposal>(`/sessions/${sessionId}/proposal`),
  })
  // The locale travels with the ask: the server has no reader and cannot know
  // which language to have the manager write its labels and reasons in.
  const analyse = useMutation({
    mutationFn: () => api.post<ChiefProposal>(`/sessions/${sessionId}/analyze`, { locale }),
    onSuccess: () => setPicked(0),
  })

  const proposal = analyse.data ?? suggestion.data
  const options = proposal?.options ?? []
  const active: ChiefOption | undefined = options[picked] ?? options[0]

  // The draft follows the proposal until the operator types, and never after
  // (the prefill rule, CLAUDE.md — `useEditable`, not a re-seeding effect).
  const draft = useEditable(proposal?.prompt ?? '')

  const send = useMutation({
    mutationFn: (prompt: string) => api.post<ChiefProposal>(`/sessions/${sessionId}/prompt`, { prompt }),
    onSuccess: () => {
      // The prompt is now on its way to the session; give the field back to the
      // next proposal rather than leaving the sent text sitting in it.
      draft.reset()
      onSent?.()
      // And give the row back. The session is working again, it re-sorts out of
      // the attention band, and an editor left open on it is one more thing to
      // close before the next session — on a screen whose job is going through
      // them one after another.
      onDismiss?.()
    },
  })

  /* The head describes whichever option is in the field, not the envelope's
     first one — otherwise picking "Bitti" leaves the badge saying "Sürdür"
     above an editor that has just disappeared. */
  const shown: ChiefAction | undefined = active?.action ?? proposal?.action
  const action = actionLabel(shown, t)
  /* The model's sentence about *this* option, else the rules' composed reason.
     A model's reasoning has no kind and is shown verbatim; only the rules'
     goes through the translation table. */
  const why = active?.why ?? (proposal ? chiefReasonSentence(proposal, t) : '')
  const note = proposal ? chiefNoteSentence(proposal, t) : ''
  /* Amber is for a degradation an operator has to go and fix — an account that
     cannot be reached, an analysis that failed. "Nobody has designated a chief
     yet" is not that: it is a suggestion, it is true on every session until it
     is acted on once, and painting it amber would put a warning on every row of
     a working deployment. This console spends colour on what needs a person
     (CLAUDE.md); a pointer to a better setting is not one. */
  const noteIsHint = proposal?.noteKind === 'no_manager'
  const holding = shown === 'HOLD'
  const finished = shown === 'FINISH'
  const analyzeUnavailable = analyse.error instanceof ApiError && analyse.error.status === 503

  const canSend = draft.value.trim() !== ''
  const submit = () => {
    if (canSend && !send.isPending) send.mutate(draft.value.trim())
  }
  // The modified Enter, both ways round, because this console is driven from a
  // Mac and from a Linux box on the same day.
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      submit()
      return
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      onDismiss?.()
    }
  }

  const said = turnSegments(turn?.reply)

  return (
    <div className="k-next">
      {/* State, reason, and the one control that changes the proposal — on one
          line, with `analyse` at the trailing edge so it lands on the same
          vertical rule as `send` below it. */}
      <div className="k-next__head">
        {shown && action && (
          <Badge tone={actionTone(shown)} subtle>
            {action}
          </Badge>
        )}
        <p className="k-next__why">
          {why}
          {/* The separator sits outside the span so the span's text is exactly
              the word — a screen reader should not read "dot modelden", and a
              test should not have to know about the punctuation. */}
          {proposal?.source === 'MODEL' && (
            <>
              {' · '}
              <span className="k-next__src k-mono">{t('chief.bymodel')}</span>
            </>
          )}
        </p>
        <Button
          className="k-next__suggest"
          size="sm"
          variant="default"
          busy={analyse.isPending}
          onClick={() => {
            // Reseed from the model's answer, not the operator's stale edit.
            draft.reset()
            analyse.mutate()
          }}
          title={t('chief.analyzeHint')}
        >
          <Icon icon={Sparkles} size={14} />
          {t('chief.analyze')}
        </Button>
      </div>

      {/* Why the rules answered rather than the model. Its own line, and its own
          voice: it is a degradation the operator can act on, not part of the
          reasoning. */}
      {note && (
        <p className={clsx('k-next__note', noteIsHint && 'k-next__note--hint')}>
          {note}
          {/* The server's own sentence, in mono because it is the machine's
              words and not the interface's (CLAUDE.md). Shown even when the
              note kind is one this build does not know, so a reason added to
              the server after this bundle still reaches the operator instead
              of being dropped on the floor. */}
          {proposal?.noteDetail && (
            <span className="k-next__notedetail k-mono"> {proposal.noteDetail}</span>
          )}
        </p>
      )}
      {!note && proposal?.noteDetail && (
        <p className="k-next__note k-mono">{proposal.noteDetail}</p>
      )}
      {analyzeUnavailable && <Notice tone="warn">{t('chief.unavailable')}</Notice>}

      {/* What is being replied to. The row above clips it to one line at a
          measure; here it is the whole of the last thing the agent said, which
          is what an operator reads before deciding what to say back. */}
      {said.segments.length > 0 && (
        <blockquote className="k-next__said">
          <span className="k-next__said-mark" aria-hidden>
            ◂
          </span>
          <span className={said.harness ? 'k-mono' : undefined}>
            <Segments of={said.segments} />
          </span>
          {turn?.replyAt && (
            <span className="k-next__said-when k-mono">{formatRelative(turn.replyAt, locale)}</span>
          )}
        </blockquote>
      )}

      {/* The manager's candidates.
       *
       * Under the quote and above the field, which is the order the decision is
       * made in: read what the agent said, choose what to say back, edit it,
       * send. Picking writes the instruction into the field rather than sending
       * it — approval is still a separate act, and the operator's own words
       * still win over every one of these (ADR-0025).
       *
       * A `role="group"` of pressed buttons rather than radios: these are not a
       * form value being submitted, they are four ways to fill one field, and
       * the field below is the thing that gets sent. */}
      {options.length > 0 && (
        <div className="k-next__options" role="group" aria-label={t('chief.options')}>
          {proposal?.accountLabel && (
            <span className="k-next__runson">
              {t('chief.runsOn')} <span className="k-mono">{proposal.accountLabel}</span>
            </span>
          )}
          {options.map((o, i) => (
            <button
              key={`${i}-${o.label ?? ''}`}
              type="button"
              className={clsx('k-next__option', i === picked && 'k-next__option--on')}
              aria-pressed={i === picked}
              title={o.prompt || o.why || undefined}
              onClick={() => {
                setPicked(i)
                draft.set(o.prompt ?? '')
              }}
            >
              {/* The kind, only where it changes what this control does.
                  CONTINUE is the resting answer — an instruction to send — and
                  printing "Sürdür" on three of four chips would be the board's
                  own rejected design in miniature: a word on everything, which
                  is a word that distinguishes nothing. FINISH, HOLD and ANSWER
                  each mean the editor behaves differently, so they are named. */}
              {o.action !== 'CONTINUE' && (
                <span className="k-next__optionkind">{actionLabel(o.action, t)}</span>
              )}
              {optionLabel(o)}
            </button>
          ))}
        </div>
      )}

      {!holding && !finished && (
        <>
          <TextAreaField
            label={t('chief.nextPrompt')}
            labelHidden
            value={draft.value}
            onChange={(v) => draft.set(v)}
            rows={3}
            placeholder={t('chief.nextPromptPlaceholder')}
            autoFocus={autoFocus}
            onKeyDown={onKey}
          />
          <div className="k-next__actions">
            <span className="k-next__hint">{t('chief.sendHint')}</span>
            {send.isSuccess && <span className="k-next__sent">{t('chief.sent')}</span>}
            <Button
              size="sm"
              variant="primary"
              busy={send.isPending}
              disabled={!canSend}
              title={canSend ? undefined : t('chief.sendEmpty')}
              onClick={submit}
            >
              <Icon icon={Send} size={14} />
              {t('chief.send')}
            </Button>
          </div>
        </>
      )}
      {finished && <p className="k-next__finished">{t('chief.finished')}</p>}

      {send.error && <Notice tone="err">{describe(send.error).title}</Notice>}
    </div>
  )
}
